import {
  PointerEvent as ReactPointerEvent,
  RefObject,
  useCallback,
  useEffect,
  useRef,
} from 'react';

import { columnWidthVariableName } from '~components/DataTable/dataTableColumnVariables';

// The main mouse button, or any touch or pen contact.
const PRIMARY_BUTTON = 0;

interface ActiveResize {
  // Ends the drag without saving and puts the column back to its width before it.
  abandon: () => void;
  pointerId: number;
}

// A drag writes the column's width variable straight onto the table, so neither React
// nor localStorage sees a pointermove. The width is saved once, when the drag ends.
// `DataTable` renders saved widths into the same `style`, and React only updates the
// properties it rendered, so the drag's value stands until the saved one replaces it.
export default function useColumnResize(
  tableRef: RefObject<HTMLTableElement | null>,
  onResizeEnd: (field: string, width: number) => void,
) {
  const activeResizeRef = useRef<ActiveResize>(undefined);

  useEffect(() => () => activeResizeRef.current?.abandon(), []);

  const startResize = useCallback(
    (
      event: ReactPointerEvent<HTMLElement>,
      field: string,
      minWidth: number,
      maxWidth = Infinity,
    ) => {
      const table = tableRef.current;
      const handle = event.currentTarget;
      const headerCell = handle.closest('th');
      const { pointerId } = event;

      // A second finger on another handle mid-drag is ignored, so the first drag
      // still saves when it ends. A new press from the same pointer means its release
      // was lost (e.g. the window lost focus), so that stale drag is dropped instead.
      const isResizingWithAnotherPointer =
        activeResizeRef.current !== undefined &&
        activeResizeRef.current.pointerId !== pointerId;

      if (
        !table ||
        !headerCell ||
        event.button !== PRIMARY_BUTTON ||
        isResizingWithAnotherPointer
      ) {
        return;
      }

      event.preventDefault();
      activeResizeRef.current?.abandon();

      const startX = event.clientX;
      const startWidth = headerCell.getBoundingClientRect().width;
      const variableName = columnWidthVariableName(field);
      const widthBeforeDrag = table.style.getPropertyValue(variableName);
      const listeners = new AbortController();
      let draggedWidth: number | undefined;
      handle.dataset.resizing = 'true';

      const stopListening = () => {
        listeners.abort();
        delete handle.dataset.resizing;
        activeResizeRef.current = undefined;
      };

      const handlePointerMove = (moveEvent: PointerEvent) => {
        if (moveEvent.pointerId !== pointerId) {
          return;
        }

        const requestedWidth = Math.round(startWidth + moveEvent.clientX - startX);
        draggedWidth = Math.min(Math.max(requestedWidth, minWidth), maxWidth);
        table.style.setProperty(variableName, `${draggedWidth}px`);
      };

      const handlePointerEnd = (endEvent: PointerEvent) => {
        if (endEvent.pointerId !== pointerId) {
          return;
        }

        stopListening();

        // A tap or click without a drag shouldn't pin the column at its current width.
        if (draggedWidth !== undefined) {
          onResizeEnd(field, draggedWidth);
        }
      };

      const abandon = () => {
        stopListening();

        if (widthBeforeDrag) {
          table.style.setProperty(variableName, widthBeforeDrag);
        } else {
          table.style.removeProperty(variableName);
        }
      };

      const { signal } = listeners;
      document.addEventListener('pointermove', handlePointerMove, { signal });
      document.addEventListener('pointerup', handlePointerEnd, { signal });
      document.addEventListener('pointercancel', handlePointerEnd, { signal });
      activeResizeRef.current = { abandon, pointerId };
    },
    [onResizeEnd, tableRef],
  );

  return { startResize };
}
